/**
 * @fileoverview Ad Platform Spend donut chart component with glassmorphism styling.
 * @module shared/components/ui/AdPlatformSpendDonut
 */

import { ReactElement, useMemo, useCallback } from 'react'
import { ChartCardHeader } from './ChartCardHeader'
import { Megaphone } from 'lucide-react'
import { ChartEmptyState } from './ChartEmptyState'
import { useTranslation } from 'react-i18next'
import { Cell, Label, Pie, PieChart } from 'recharts'
import { Card } from '@/components/ui/card'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui'
import { useAdPlatformSpend } from '@/shared/hooks/useAdPlatformSpend'
import { useEcommerceTotals } from '@/shared/hooks/dashboard'
import { getDonutChartColors, formatPercentage } from '@/components/ui/chart-utils'
import { cn } from '@/shared/utils/cn'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import type { ChartConfig } from '@/components/ui/chart'
import { RechartsHoverAdapter } from './RechartsHoverAdapter'
import { AnchoredHoverCard } from './AnchoredHoverCard'
import { useAggregatedProductMetrics } from '@/shared/hooks/dashboard/useAggregatedProductMetrics'
import { useProductGroupsMetadata } from '@/shared/hooks'
import { useDonutTouchInteraction, renderStaticActiveShape } from '@/shared/hooks/useDonutTouchInteraction'
import { renderActiveDonutSector } from '@/shared/components/ui/AnimatedDonutSector'
import { DonutSliceGradients } from '@/shared/components/ui/DonutSliceGradients'
import { donutSliceGradientFill } from '@/shared/utils/donutGradient'
import { Thumbnail } from './Thumbnail'
import { BsMeta } from 'react-icons/bs'
import { GoogleAdsIcon } from '@/shared/components/ui/icons/GoogleAdsIcon'
import { useIntegrationsStatus } from '@/features/settings/hooks/useIntegrationsStatus'
import { FacebookOAuthConnect } from '@/features/settings/components/FacebookOAuthConnect'
import { GoogleAdsOAuthConnect } from '@/features/settings/components/GoogleAdsOAuthConnect'

/**
 * Chart data point for ad platform spend visualization
 */
interface AdPlatformSpendData {
  /** Platform name */
  name: string
  /** Spend amount */
  value: number
  /** Fill color for the pie slice */
  fill: string
  /** Percentage of total */
  percentage: number
  /** Index signature for recharts compatibility */
  [key: string]: unknown
}

/**
 * Props for AdPlatformSpendDonut component
 */
interface AdPlatformSpendDonutProps {
  /** Additional CSS classes */
  className?: string
}

/**
 * Map platform names to display names and colors
 */
function getPlatformDisplayName(platform: string): string {
  const mapping: Record<string, string> = {
    meta: 'Meta Ads',
    facebook: 'Meta Ads',
    google: 'Google Ads',
    googleads: 'Google Ads',
    influencers: 'Influencers',
    influencer: 'Influencers',
  }

  const normalized = platform.toLowerCase()
  return mapping[normalized] ?? platform
}

/**
 * NOTE: Intentionally removed Tailwind arbitrary bg classes for legend.
 * We rely on resolved inline styles from getPlatformColor() to ensure
 * legend swatches exactly match Chart.js slice colors and avoid purge risks.
 */

/**
 * Creates chart configuration for ShadCN charts
 */
const createChartConfig = (data: AdPlatformSpendData[]): ChartConfig => {
  const config: ChartConfig = {}
  data.forEach(item => {
    const key = item.name.toLowerCase().replace(/\s+/g, '-')
    config[key] = {
      label: item.name,
      color: item.fill,
    }
  })
  return config
}

const platformKeyAliases: Record<string, string> = {
  'meta ads': 'meta',
  meta: 'meta',
  facebook: 'meta',
  'google ads': 'google',
  google: 'google',
  influencers: 'influencers',
  influencer: 'influencers',
}

const pickPlatformKey = (
  displayName: string,
  platforms: Record<string, number> | undefined
): string | null => {
  if (!platforms) return null
  const entries = Object.keys(platforms)
  const lc = entries.map(k => [k, k.toLowerCase()] as const)
  const desired = platformKeyAliases[displayName.toLowerCase()] ?? displayName.toLowerCase()
  const exact = lc.find(([, low]) => low === desired)
  if (exact) return exact[0]
  const contains =
    lc.find(([, low]) => low.includes('meta') && desired.includes('meta')) ||
    lc.find(([, low]) => low.includes('google') && desired.includes('google')) ||
    lc.find(([, low]) => low.includes('influenc'))
  return contains ? contains[0] : null
}

/**
 * Tooltip item for ad platform spend breakdown
 */
interface AdPlatformTooltipItem {
  key: string
  name: string
  icon?: ReactElement | undefined
  value: number
  percentage?: string | undefined
}

/**
 * Builds the table-based tooltip content for AdPlatformSpendDonut
 */
function AdPlatformTooltipContent({
  platformName,
  platformTotal,
  platformColor,
  platformIcon,
  items,
  formatValue,
}: {
  platformName: string
  platformTotal: number
  platformColor: string
  platformIcon: ReactElement | null
  items: AdPlatformTooltipItem[]
  formatValue: (value: number, maxDigits?: number) => string
}): ReactElement {
  if (!items.length) {
    return (
      <div className="text-base font-medium text-center p-4 text-muted-foreground">
        No products for {platformName}
      </div>
    )
  }

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow className="hover:bg-transparent border-t-0 bg-black/10">
          <TableHead className="pl-4 pr-2 h-8">
            <div className="flex items-center gap-2">
              {platformIcon && (
                <span
                  className="shrink-0"
                  style={{
                    color: platformName.toLowerCase().includes('google')
                      ? undefined
                      : platformColor,
                  }}
                >
                  {platformIcon}
                </span>
              )}
              <span className="text-xs font-medium text-muted-foreground">
                {platformName}
              </span>
            </div>
          </TableHead>
          <TableHead className="w-36 pl-2 pr-4 h-8 text-right">
            <div className="flex items-center justify-end gap-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: platformColor }}
              />
              <span className="text-xs font-semibold text-foreground tabular-nums">
                {formatValue(platformTotal)}
              </span>
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.key} className="hover:bg-transparent">
            <TableCell className="pl-4 pr-2 py-2 align-middle">
              <div className="flex items-center gap-2 min-w-0">
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span className="truncate text-sm text-muted-foreground">{item.name}</span>
              </div>
            </TableCell>
            <TableCell className="w-36 pl-2 pr-4 py-2 text-right align-middle">
              <div className="flex flex-col items-end">
                <span className="text-sm font-medium tabular-nums text-foreground whitespace-nowrap">
                  {formatValue(item.value)}
                </span>
                {item.percentage && (
                  <span className="text-xs text-muted-foreground/60">
                    {item.percentage}
                  </span>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Ad Platform Spend donut chart component displaying spend breakdown by advertising platform.
 * Matches the mockup design with center labels and legend below.
 *
 * @component
 * @example
 * ```tsx
 * <AdPlatformSpendDonut className="w-full" />
 * ```
 */
export function AdPlatformSpendDonut({ className }: AdPlatformSpendDonutProps): ReactElement {
  const { t } = useTranslation('features-home')
  const { data, totalSpend, isLoading, error } = useAdPlatformSpend()

  // Touch interaction hook for mobile/tablet
  const {
    isMobile,
    isTablet,
    isBelowDesktop,
    mobileSlice,
    tabletSlice,
    tabletAnchor,
    chartContainerRef,
    handleSliceClick,
    clearMobileSlice,
  } = useDonutTouchInteraction<AdPlatformSpendData>()

  // Stable fallbacks so hooks (useMemo) run in same order always
  const safeData = useMemo(() => data ?? [], [data])
  const safeTotalSpend = totalSpend ?? 1
  const compactMoney = useCompactMoney()
  const formatValue = useCallback(
    (value: number, maxDigits = 1): string => compactMoney(value, maxDigits),
    [compactMoney]
  )

  const { data: ecommerceTotals } = useEcommerceTotals()
  const roas = ecommerceTotals && safeTotalSpend > 0
    ? ecommerceTotals.revenue / safeTotalSpend
    : null

  const colors = getDonutChartColors()
  const { products } = useAggregatedProductMetrics()
  const { getGroup } = useProductGroupsMetadata()
  const integrationsQuery = useIntegrationsStatus()

  // Local platform color resolver
  const platformColor = useCallback(
    (platform: string): string => {
      const normalizedPlatform = platform.toLowerCase()

      if (normalizedPlatform.includes('meta') || normalizedPlatform.includes('facebook')) {
        return colors.meta
      }
      if (normalizedPlatform.includes('google')) {
        return colors.google
      }
      if (normalizedPlatform.includes('influencer')) {
        return colors.influencers
      }

      // Fallback colors for other platforms
      const fallbackColors = [colors.cogs, colors.adSpend, colors.profit]
      const hash = platform.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      return fallbackColors[hash % fallbackColors.length] ?? colors.cogs
    },
    [colors]
  )

  /**
   * Get platform icon based on normalized platform name
   */
  const getPlatformIcon = (platform: string): ReactElement | null => {
    const normalized = platform.toLowerCase()
    if (normalized.includes('meta') || normalized.includes('facebook')) {
      return <BsMeta className="w-5 h-5" aria-hidden="true" />
    }
    if (normalized.includes('google')) {
      return <GoogleAdsIcon className="w-5 h-5" />
    }
    return null
  }

  // Transform data for Recharts format
  const chartData: AdPlatformSpendData[] = useMemo(
    () =>
      safeData.map(item => ({
        name: getPlatformDisplayName(item.platform),
        value: item.amount,
        fill: platformColor(item.platform),
        percentage: item.percentage,
      })),
    [safeData, platformColor]
  )

  // Create chart config for ShadCN
  const chartConfig = useMemo(() => createChartConfig(chartData), [chartData])

  const buildPlatformTooltipData = useCallback(
    (displayName: string, platformTotal: number) => {
      const list = [...products]
        .map(p => {
          const key = pickPlatformKey(displayName, p.adPlatformBreakdown)
          const amount = key ? p.adPlatformBreakdown![key] : 0
          return { sku: p.sku, name: p.name, amount: Number(amount || 0) }
        })
        .filter(x => x.amount > 0)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 8)

      const sliceColor = chartData.find(d => d.name === displayName)?.fill ?? colors.adSpend

      const items: AdPlatformTooltipItem[] = list.map((row) => {
        const group = getGroup(row.sku)
        const img = group?.imageUrl ?? null
        const percentage =
          platformTotal > 0
            ? formatPercentage((Number(row.amount) / platformTotal) * 100)
            : undefined
        return {
          key: row.sku,
          name: row.name,
          icon: <Thumbnail src={img} alt={row.name} size={40} />,
          value: row.amount,
          percentage,
        }
      })

      const platformIcon = getPlatformIcon(displayName)

      return { items, sliceColor, platformIcon }
    },
    [chartData, colors.adSpend, getGroup, products]
  )

  // Now handle loading / error / empty states AFTER hooks/memos so hook order stays stable.
  if (isLoading) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.adPlatformSpend.title')}
          href="/dashboard/performance?columns=adSpend,metaAds,googleAds,roas,sales&sort=adSpend:desc"
        />
        <div className="px-6 pt-4 pb-8 space-y-8">
          <div className="flex justify-center">
            <Skeleton className="h-64 w-64 rounded-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.adPlatformSpend.title')}
          href="/dashboard/performance?columns=adSpend,metaAds,googleAds,roas,sales&sort=adSpend:desc"
        />
        <div className="px-6 pt-4 pb-8">
          <div className="flex items-center justify-center h-48 text-destructive">
            <p>{t('charts.adPlatformSpend.error')}</p>
          </div>
        </div>
      </Card>
    )
  }

  if (safeData.length === 0 || safeTotalSpend === 0) {
    // Check if any ad integrations are connected
    const integrations = integrationsQuery.data ?? []
    const facebookStatus = integrations.find(i => i.provider === 'FACEBOOK')
    const googleStatus = integrations.find(i => i.provider === 'GOOGLE_ADS')
    const hasNoAdIntegrations =
      (!facebookStatus || facebookStatus.status === 'NOT_CONFIGURED') &&
      (!googleStatus || googleStatus.status === 'NOT_CONFIGURED')

    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.adPlatformSpend.title')}
          href="/dashboard/performance?columns=adSpend,metaAds,googleAds,roas,sales&sort=adSpend:desc"
        />
        <div className="px-6 pt-4 pb-8">
          {hasNoAdIntegrations ? (
            <ChartEmptyState
              variant="donut"
              icon={Megaphone}
              message={t('charts.adPlatformSpend.connectPrompt', {
                defaultValue: 'Connect your ad platforms to see spend data',
              })}
              className="aspect-square"
            >
              <FacebookOAuthConnect
                buttonLabel={t('charts.adPlatformSpend.connectFacebook', {
                  defaultValue: 'Connect Facebook Ads',
                })}
                buttonIcon={<img src="/meta-favicon.png" alt="" className="size-4" />}
              />
              <GoogleAdsOAuthConnect
                buttonLabel={t('charts.adPlatformSpend.connectGoogle', {
                  defaultValue: 'Connect Google Ads',
                })}
                buttonIcon={<img src="/google-favicon.png" alt="" className="size-4" />}
              />
            </ChartEmptyState>
          ) : (
            <ChartEmptyState
              variant="donut"
              icon={Megaphone}
              message={t('charts.adPlatformSpend.noData')}
              hint={t('charts.emptyHint')}
              className="h-48"
            />
          )}
        </div>
      </Card>
    )
  }

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
      {/* Header section */}
      <ChartCardHeader
        title={t('charts.adPlatformSpend.title')}
        href="/dashboard/performance?columns=adSpend,metaAds,googleAds,roas,sales&sort=adSpend:desc"
        trailing={
          roas !== null ? (
            <span className="flex items-baseline gap-1">
              <span className="text-base font-bold font-mono text-foreground">
                {roas.toFixed(1)}x
              </span>
              <span className="text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground/60">
                ROAS
              </span>
            </span>
          ) : undefined
        }
      />
      {/* Chart section with border separator */}
      <div ref={chartContainerRef} className="px-6 pt-4 pb-8">
        {/* Donut Chart with Center Labels */}
        <ChartContainer
          id="ad-platform-spend-donut"
          config={chartConfig}
          className="mx-auto aspect-[1/1] w-full [&_.recharts-surface]:overflow-visible"
        >
          <PieChart margin={{ top: 20, left: 20, right: 20, bottom: 20 }}>
            <DonutSliceGradients idPrefix="ad-platform-slice" colors={chartData.map((d) => d.fill)} />
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="70%"
              paddingAngle={4}
              cornerRadius={8}
              dataKey="value"
              stroke="none"
              isAnimationActive={false}
              activeShape={isBelowDesktop ? renderStaticActiveShape : renderActiveDonutSector}
              onClick={(_, index) => {
                const slice = chartData[index]
                if (slice) handleSliceClick(slice)
              }}
              labelLine={{
                stroke: 'var(--muted-foreground)',
                strokeWidth: 1,
                strokeDasharray: '3 3',
              }}
              label={({ cx, cy, midAngle = 0, outerRadius, payload }) => {
                const RADIAN = Math.PI / 180
                const valueText = formatValue(payload.value)
                const shortLabel =
                  (payload.name as string | undefined) ??
                  t('charts.adPlatformSpend.shortLabel', { defaultValue: 'Ads' })
                const radius = Number(outerRadius) + (isBelowDesktop ? 16 : 20)
                const x = cx + radius * Math.cos(-midAngle * RADIAN)
                const y = cy + radius * Math.sin(-midAngle * RADIAN)
                const isRightSide = Math.cos(-midAngle * RADIAN) > 0

                // On touch: show value + short label to avoid clipping
                if (isBelowDesktop) {
                  return (
                    <g>
                      <text
                        x={x}
                        y={y - 6}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        dominantBaseline="middle"
                        className="font-bold fill-foreground"
                        style={{ fontSize: 14 }}
                      >
                        {valueText}
                      </text>
                      <text
                        x={x}
                        y={y + 12}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        className="fill-muted-foreground"
                        style={{ fontSize: 12 }}
                      >
                        {shortLabel}
                      </text>
                    </g>
                  )
                }

                // Split text into two lines if it's too long (> 18 characters)
                const labelText = `${payload.name} ${payload.percentage.toFixed(1)}%`
                const MAX_LENGTH = 18
                const shouldWrap = labelText.length > MAX_LENGTH

                let line1 = labelText
                let line2 = ''

                if (shouldWrap) {
                  // Find a good split point (prefer splitting at space)
                  const words = payload.name.split(' ')
                  const percentage = `${payload.percentage.toFixed(1)}%`

                  if (words.length > 1) {
                    // Multi-word label: put percentage on second line
                    line1 = payload.name
                    line2 = percentage
                  } else {
                    // Single word: try to split at middle
                    const mid = Math.floor(labelText.length / 2)
                    const spaceIndex = labelText.lastIndexOf(' ', mid)
                    if (spaceIndex > 0) {
                      line1 = labelText.substring(0, spaceIndex)
                      line2 = labelText.substring(spaceIndex + 1)
                    }
                  }
                }

                return (
                  <g>
                    <text
                      x={x}
                      y={shouldWrap ? y - 21 : y - 15}
                      textAnchor={isRightSide ? 'start' : 'end'}
                      dominantBaseline="middle"
                      className="font-bold fill-foreground"
                      style={{ fontSize: 16 }}
                    >
                      {valueText}
                    </text>
                    <g transform={`translate(${x}, ${shouldWrap ? y + 9 : y + 5})`}>
                      <circle
                        cx={isRightSide ? 5 : -5}
                        cy={shouldWrap ? -6 : 0}
                        r={3}
                        fill={payload.fill}
                      />
                      <text
                        x={isRightSide ? 12 : -12}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        className="fill-muted-foreground"
                        style={{ fontSize: 13 }}
                      >
                        <tspan x={isRightSide ? 12 : -12} dy={shouldWrap ? '-0.6em' : '0'} dominantBaseline="middle">
                          {line1}
                        </tspan>
                        {shouldWrap && (
                          <tspan x={isRightSide ? 12 : -12} dy="1.2em" dominantBaseline="middle">
                            {line2}
                          </tspan>
                        )}
                      </text>
                    </g>
                  </g>
                )
              }}
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={donutSliceGradientFill('ad-platform-slice', index)} />
              ))}
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 10}
                          className={cn('font-bold', isBelowDesktop ? 'text-xl' : 'text-3xl')}
                          fill="var(--foreground)"
                        >
                          {formatValue(safeTotalSpend)}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 8}
                          className={cn('block', isBelowDesktop ? 'text-xs' : 'text-sm')}
                          fill="var(--muted-foreground)"
                        >
                          {t('charts.adPlatformSpend.totalAdSpendLine1', {
                            defaultValue: 'Ad',
                          })}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 22}
                          className={cn('block', isBelowDesktop ? 'text-xs' : 'text-sm')}
                          fill="var(--muted-foreground)"
                        >
                          {t('charts.adPlatformSpend.totalAdSpendLine2', {
                            defaultValue: 'Spend',
                          })}
                        </tspan>
                      </text>
                    )
                  }
                }}
              />
            </Pie>
            <ChartTooltip
              cursor={false}
              content={
                <RechartsHoverAdapter
                  dataChartId="chart-ad-platform-spend-donut"
                  width={450}
                  estimatedHeight={420}
                  disabled={isBelowDesktop}
                  animatePosition={false}
                >
                  {({ payload }) => {
                    type SlicePayload = { value?: number; name?: string }
                    const item: SlicePayload | null =
                      Array.isArray(payload) && payload.length
                        ? (payload[0] as SlicePayload)
                        : null
                    if (!item) return null
                    const displayName = String(item.name ?? '')
                    const platformTotal = Number(item.value ?? 0)
                    if (!displayName || platformTotal === 0) return null

                    // Normalize display names back to platform keys used in adPlatformBreakdown
                    const pickPlatformKey = (
                      platforms: Record<string, number> | undefined
                    ): string | null => {
                      if (!platforms) return null
                      const entries = Object.keys(platforms)
                      const lc = entries.map(k => [k, k.toLowerCase()] as const)
                      const s = displayName.toLowerCase()
                      const table: Record<string, string> = {
                        'meta ads': 'meta',
                        meta: 'meta',
                        facebook: 'meta',
                        'google ads': 'google',
                        google: 'google',
                        influencers: 'influencers',
                        influencer: 'influencers',
                      }
                      const desired = table[s] || s
                      // 1) exact case-insensitive match
                      const exact = lc.find(([, low]) => low === desired)
                      if (exact) return exact[0]
                      // 2) substring heuristic
                      const contains =
                        lc.find(([, low]) => low.includes('meta') && desired.includes('meta')) ||
                        lc.find(
                          ([, low]) => low.includes('google') && desired.includes('google')
                        ) ||
                        lc.find(([, low]) => low.includes('influenc'))
                      return contains ? contains[0] : null
                    }

                    const list = [...products]
                      .map(p => {
                        const key = pickPlatformKey(p.adPlatformBreakdown)
                        const amount = key ? p.adPlatformBreakdown![key] : 0
                        return { sku: p.sku, name: p.name, amount: Number(amount || 0) }
                      })
                      .filter(x => x.amount > 0)
                      .sort((a, b) => b.amount - a.amount)
                      .slice(0, 8)

                    const platformColor =
                      chartData.find(d => d.name === displayName)?.fill ?? colors.adSpend

                    const items: AdPlatformTooltipItem[] = list.map((row) => {
                      const group = getGroup(row.sku)
                      const img = group?.imageUrl ?? null
                      const percentage =
                        platformTotal > 0
                          ? formatPercentage((Number(row.amount) / platformTotal) * 100)
                          : undefined
                      return {
                        key: row.sku,
                        name: row.name,
                        icon: <Thumbnail src={img} alt={row.name} size={40} />,
                        value: row.amount,
                        percentage,
                      }
                    })

                    const platformIcon = getPlatformIcon(displayName)

                    return (
                      <AdPlatformTooltipContent
                        platformName={displayName}
                        platformTotal={platformTotal}
                        platformColor={platformColor}
                        platformIcon={platformIcon}
                        items={items}
                        formatValue={formatValue}
                      />
                    )
                  }}
                </RechartsHoverAdapter>
              }
              wrapperStyle={{ display: 'none' }}
            />
          </PieChart>
        </ChartContainer>
      </div>

      {/* Mobile Sheet for tooltip content */}
      {isMobile && (
        <Sheet open={!!mobileSlice} onOpenChange={(open) => !open && clearMobileSlice()}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className={cn(
              'max-h-[85vh] overflow-y-auto rounded-t-2xl',
              'bg-[var(--glass-surface-dark)]'
            )}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{mobileSlice?.name ?? 'Details'}</SheetTitle>
              <SheetDescription>Breakdown by product</SheetDescription>
            </SheetHeader>
            <div className="pb-safe">
              {mobileSlice && (() => {
                const displayName = mobileSlice.name
                const platformTotal = mobileSlice.value

                const { items, sliceColor, platformIcon } = buildPlatformTooltipData(
                  displayName,
                  platformTotal
                )

                return (
                  <AdPlatformTooltipContent
                    platformName={displayName}
                    platformTotal={platformTotal}
                    platformColor={sliceColor}
                    platformIcon={platformIcon}
                    items={items}
                    formatValue={formatValue}
                  />
                )
              })()}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Tablet popover - uses AnchoredHoverCard positioned at chart center */}
      {isTablet && tabletSlice && tabletAnchor && (() => {
        const displayName = tabletSlice.name
        const platformTotal = tabletSlice.value

        const { items, sliceColor, platformIcon } = buildPlatformTooltipData(
          displayName,
          platformTotal
        )

        return (
          <AnchoredHoverCard
            active={true}
            anchor={tabletAnchor}
            width={450}
            estimatedHeight={420}
            margin={8}
            offset={{ x: 0, y: 0 }}
            inert={false}
          >
            <AdPlatformTooltipContent
              platformName={displayName}
              platformTotal={platformTotal}
              platformColor={sliceColor}
              platformIcon={platformIcon}
              items={items}
              formatValue={formatValue}
            />
          </AnchoredHoverCard>
        )
      })()}
    </Card>
  )
}
